import { $, $$ } from './utils';
import { getPersistentItem, setPersistentItem } from './persistence';
import { initCarouselContainer, syncMobileCarousels } from './carousel/carousel';

const shiftTriggerDesktopMql = window.matchMedia('(min-width: 1004px)');

/** Desktop-only tooltip trigger; inline shift copy on smaller viewports. */
const syncShiftTriggerTabFocus = () => {
    const focusable = shiftTriggerDesktopMql.matches;
    $$('.job-postings-shift-trigger').forEach((trigger) => {
        trigger.tabIndex = focusable ? 0 : -1;
        trigger.toggleAttribute('aria-hidden', !focusable);
    });
};

const initTooltips = function () {
    syncShiftTriggerTabFocus();

    if (!window.matchMedia('(min-width: 570px)').matches) return;

    const closeAllTooltips = () => {
        $$('.job-postings-shift-req.show').forEach((el) => el.classList.remove('show'));
        document
            .querySelectorAll('.job-postings-shift-trigger[aria-expanded="true"]')
            .forEach((el) => el.setAttribute('aria-expanded', 'false'));
    };

    const toggleTooltip = (trigger, show = true) => {
        const wrapper = trigger.closest('div');
        const tooltip = wrapper?.querySelector('.job-postings-shift-req');
        if (!tooltip) return;

        if (show) {
            closeAllTooltips();
            tooltip.classList.add('show');
            trigger.setAttribute('aria-expanded', 'true');
        } else {
            tooltip.classList.remove('show');
            trigger.setAttribute('aria-expanded', 'false');
        }
    };

    $$('.job-postings-shift-trigger').forEach((trigger) => {
        const wrapper = trigger.closest('div');
        const tooltip = wrapper?.querySelector('.job-postings-shift-req');
        if (!tooltip) return;

        trigger.addEventListener('mouseenter', () => toggleTooltip(trigger, true));
        trigger.addEventListener('mouseleave', (e) => {
            if (tooltip.contains(e.relatedTarget)) return;
            toggleTooltip(trigger, false);
        });

        tooltip.addEventListener('mouseleave', (e) => {
            if (trigger.contains(e.relatedTarget)) return;
            toggleTooltip(trigger, false);
        });

        trigger.addEventListener('focus', () => toggleTooltip(trigger, true));
        trigger.addEventListener('blur', () => toggleTooltip(trigger, false));
    });
};

const initIntersectionObserver = function () {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                setPersistentItem('has_seen_job_posting', true);
            }
        });
    });

    const buttons = $$('.job-postings-button');
    buttons.forEach((button) => {
        observer.observe(button);
    });
};

export const getHasSeenJobPosting = function () {
    return getPersistentItem('has_seen_job_posting') === true;
};

const getFilteredJobPostings = (allJobPostings, cityId) => {
    if (!cityId) {
        // All locations - return first posting from each top city
        return allJobPostings.filter((posting) => posting.isFirstForTopCity);
    }
    // Specific city - return all postings for that city
    return allJobPostings.filter((posting) => String(posting.cityId) === String(cityId));
};

const buildCarouselHtml = (filteredPostings, descriptionText) => {
    if (!filteredPostings.length) return '';

    const slidesHtml = filteredPostings
        .map(
            (posting, index) => `
            <div class="carousel__slide${index === 0 ? ' is-active' : ''}" data-city-id="${posting.cityId}">
                ${posting.cardHtml}
            </div>
        `,
        )
        .join('');

    const bulletsHtml =
        filteredPostings.length > 1
            ? `<div class="carousel__nav">
                <div class="carousel__bullets">
                    ${filteredPostings
                        .map((_, index) => `<span class="carousel__bullet${index === 0 ? ' is-active' : ''}"></span>`)
                        .join('')}
                </div>
            </div>`
            : '';

    const hasWorkingHours = filteredPostings.some((p) => p.cardHtml.includes('job-postings-shift-req'));
    const descriptionHtml =
        hasWorkingHours && descriptionText ? `<p class="job-postings-description">*${descriptionText}</p>` : '';

    return `
        <div class="job-posting-cards">
            <div class="carousel--mobile-only" data-mobile-only="1" data-mobile-breakpoint="1003">
                <div class="row">
                    <div class="carousel__slides">
                        ${slidesHtml}
                    </div>
                </div>
                ${descriptionHtml}
                ${bulletsHtml}
            </div>
        </div>
    `;
};

const handleJobPostingLocationFilter = function () {
    const jobPostingsSection = $('#job-postings');
    const locationSelector = $('#job-postings-location-selector');
    if (!jobPostingsSection || !locationSelector) return;

    const jobPostingsRow = $('#job-postings-row');
    const emptyState = $('#job-postings-empty-state');

    // Parse all job postings data from the section
    const allJobPostingsJson = jobPostingsSection.dataset.allJobPostings;
    const descriptionText = jobPostingsSection.dataset.descriptionText || '';

    if (!allJobPostingsJson) return;

    let allJobPostings;
    try {
        allJobPostings = JSON.parse(allJobPostingsJson);
    } catch (e) {
        console.error('Failed to parse job postings data:', e);
        return;
    }

    const cityMetadata = locationSelector.dataset.cities ? JSON.parse(locationSelector.dataset.cities) : [];

    const getSelectedCityId = () => {
        return locationSelector.getAttribute('data-selected-city-id') || '';
    };

    const showEmptyState = (city, variant) => {
        if (!emptyState) return;

        const activeCard = emptyState.querySelector('[data-variant="active"]');
        const inactiveCard = emptyState.querySelector('[data-variant="inactive"]');

        if (activeCard) activeCard.style.display = 'none';
        if (inactiveCard) inactiveCard.style.display = 'none';

        const card = variant === 'inactive' ? inactiveCard : activeCard;
        if (card) {
            card.querySelectorAll(
                '.job-postings-empty-title, .job-postings-empty-description, .job-postings-empty-cta span',
            ).forEach((el) => {
                if (!el.dataset.template) el.dataset.template = el.textContent;
                el.textContent = el.dataset.template.replace(/:city/g, city.name);
            });

            if (variant === 'active') {
                const ctaLink = card.querySelector('.job-postings-empty-cta--link');
                if (ctaLink && city.apply_link) {
                    const url = new URL(city.apply_link, window.location.origin);
                    url.searchParams.delete('applied_through_job_posting');
                    ctaLink.href = url.toString();
                }
            }

            card.style.display = 'block';
        }

        emptyState.style.display = 'block';
        emptyState.classList.add('is-visible');
        if (jobPostingsRow) jobPostingsRow.style.display = 'none';
    };

    const hideEmptyState = () => {
        if (!emptyState) return;
        emptyState.style.display = 'none';
        emptyState.classList.remove('is-visible');
    };

    const renderJobPostings = (cityId) => {
        const city = cityMetadata.find((c) => String(c.id) === String(cityId));

        // Prioritize inactive city state over postings or active empty states.
        if (city && !city.active) {
            showEmptyState(city, 'inactive');
            return;
        }

        const filteredPostings = getFilteredJobPostings(allJobPostings, cityId);

        if (filteredPostings.length === 0) {
            // Show empty state
            if (city) {
                showEmptyState(city, 'active');
            }
            return;
        }

        hideEmptyState();

        // Build and render the carousel HTML
        const carouselHtml = buildCarouselHtml(filteredPostings, descriptionText);

        if (jobPostingsRow) {
            jobPostingsRow.innerHTML = carouselHtml;
            jobPostingsRow.style.display = '';

            // Initialize the new carousel
            const newCarousel = jobPostingsRow.querySelector('.carousel--mobile-only');
            if (newCarousel) {
                initCarouselContainer(newCarousel);
                syncMobileCarousels();
            }

            initTooltips();
            initIntersectionObserver();
        }
    };

    const onCityChange = (event) => {
        const cityId = event?.detail?.cityId !== undefined ? String(event.detail.cityId) : getSelectedCityId();
        renderJobPostings(cityId);
    };

    // Event listeners
    window.addEventListener('job-postings:city-selected', onCityChange);

    // Empty state CTA button clears selection (back to "All locations")
    const clearBtn = emptyState?.querySelector('.job-postings-empty-cta--button');
    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            window.dispatchEvent(new CustomEvent('job-postings:clear-selection'));
        });
    }
};

export const initJobPostings = function () {
    const run = () => {
        if ($('#job-postings')) {
            initTooltips();
            initIntersectionObserver();
            handleJobPostingLocationFilter();
            shiftTriggerDesktopMql.addEventListener('change', syncShiftTriggerTabFocus);
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }
};
